import React from 'react';

const NAVY  = '#1a2a4a';
const GOLD  = '#b8860b';
const LGOLD = '#fef3c7';
const LBLUE = '#e8f0fb';
const WHITE = '#ffffff';

function Box({ x, y, w, h, fill = LBLUE, stroke = NAVY, rx = 8, children }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={rx} fill={fill} stroke={stroke} strokeWidth={1.5} />
      {children}
    </g>
  );
}

function CText({ x, y, text, size = 11, fill = NAVY, bold = false, italic = false }) {
  return (
    <text x={x} y={y} textAnchor="middle" dominantBaseline="middle"
      fontSize={size} fill={fill} fontWeight={bold ? 700 : 400}
      fontStyle={italic ? 'italic' : 'normal'}
      fontFamily="'Outfit', sans-serif">
      {text}
    </text>
  );
}

function Arrow({ x1, y1, x2, y2, label, color = GOLD }) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  return (
    <g>
      <defs>
        <marker id={`arr-${color.replace('#','')}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill={color} />
        </marker>
      </defs>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={2}
        markerEnd={`url(#arr-${color.replace('#','')})`} />
      {label && <CText x={mx} y={my - 10} text={label} size={9} fill={color} bold />}
    </g>
  );
}

// ── 1. Not Only…But Also ─────────────────────────────────────────────
function DiagramNotOnly() {
  return (
    <svg viewBox="0 0 380 130" width="100%" style={{ display: 'block' }}>
      <Box x={10} y={30} w={140} h={48} fill={LBLUE} stroke={NAVY}>
        <CText x={80}  y={51} text="POINT 1" size={10} fill={NAVY} bold />
        <CText x={80}  y={65} text="(good point)" size={9} fill="#5a6a88" italic />
      </Box>
      <CText x={205} y={55} text="STRONGER" size={9} fill={GOLD} bold />
      <Arrow x1={155} y1={54} x2={228} y2={54} color={GOLD} />
      <Box x={230} y={30} w={140} h={48} fill={LGOLD} stroke={GOLD}>
        <CText x={300} y={51} text="POINT 2" size={10} fill={NAVY} bold />
        <CText x={300} y={65} text="(stronger impact)" size={9} fill="#92400e" italic />
      </Box>
      {/* formula strip */}
      <rect x={10} y={95} width={360} height={24} rx={5} fill={NAVY} />
      <text x={190} y={108} textAnchor="middle" dominantBaseline="middle"
        fontSize={10} fill={WHITE} fontFamily="'Courier New', monospace">
        Not only does [SUBJECT] [POINT 1], but it also [POINT 2].
      </text>
    </svg>
  );
}

// ── 2. Relative Clauses ──────────────────────────────────────────────
function DiagramRelative() {
  return (
    <svg viewBox="0 0 380 150" width="100%" style={{ display: 'block' }}>
      {/* Main claim box */}
      <Box x={10} y={20} w={360} h={40} fill={LBLUE} stroke={NAVY}>
        <CText x={190} y={41} text="MAIN CLAIM" size={11} fill={NAVY} bold />
      </Box>
      {/* dotted line down */}
      <line x1={190} y1={60} x2={190} y2={85} stroke={NAVY} strokeWidth={1.5} strokeDasharray="4,3" />
      {/* relative pronoun bubble */}
      <ellipse cx={190} cy={91} rx={38} ry={13} fill={GOLD} />
      <CText x={190} y={91} text="which / who / whose / where" size={8.5} fill={WHITE} bold />
      {/* detail box */}
      <line x1={190} y1={104} x2={190} y2={115} stroke={NAVY} strokeWidth={1.5} strokeDasharray="4,3" />
      <Box x={60} y={115} w={260} h={26} fill="#f0faf4" stroke="#16a34a">
        <CText x={190} y={129} text="SUPPORTING DETAIL / EXAMPLE" size={9.5} fill="#14532d" bold />
      </Box>
    </svg>
  );
}

// ── 3. Conditionals ──────────────────────────────────────────────────
function DiagramConditional() {
  return (
    <svg viewBox="0 0 380 130" width="100%" style={{ display: 'block' }}>
      <Box x={10} y={25} w={130} h={50} fill="#fff5f5" stroke="#dc2626">
        <CText x={75} y={44} text="IF / CONDITION" size={10} fill="#991b1b" bold />
        <CText x={75} y={60} text="(were to…)" size={9} fill="#dc2626" italic />
      </Box>
      <Arrow x1={145} y1={50} x2={195} y2={50} color={GOLD} />
      <Box x={197} y={25} w={90} h={50} fill={LGOLD} stroke={GOLD}>
        <CText x={242} y={44} text="THEN" size={11} fill={NAVY} bold />
        <CText x={242} y={60} text="(consequence)" size={9} fill="#92400e" italic />
      </Box>
      <Arrow x1={290} y1={50} x2={330} y2={50} color={NAVY} />
      <Box x={332} y={25} w={40} h={50} fill={LBLUE} stroke={NAVY}>
        <CText x={352} y={50} text="RESULT" size={9} fill={NAVY} bold />
      </Box>
      <rect x={10} y={92} width={360} height={24} rx={5} fill={NAVY} />
      <text x={190} y={105} textAnchor="middle" dominantBaseline="middle"
        fontSize={10} fill={WHITE} fontFamily="'Courier New', monospace">
        If [CONDITION] were to [ACTION], [RESULT] would [OUTCOME].
      </text>
    </svg>
  );
}

// ── 4. Contrast & Concession ─────────────────────────────────────────
function DiagramContrast() {
  return (
    <svg viewBox="0 0 380 140" width="100%" style={{ display: 'block' }}>
      <Box x={10} y={20} w={155} h={52} fill="#fff5f5" stroke="#dc2626">
        <CText x={87}  y={38} text="CONCESSION" size={10} fill="#991b1b" bold />
        <CText x={87}  y={53} text="acknowledge opposing" size={9} fill="#dc2626" italic />
        <CText x={87}  y={65} text="view honestly" size={9} fill="#dc2626" italic />
      </Box>
      <ellipse cx={196} cy={46} rx={30} ry={16} fill={NAVY} />
      <CText x={196} y={46} text="Although / While / Despite" size={7.5} fill={WHITE} bold />
      <Box x={220} y={20} w={152} h={52} fill={LGOLD} stroke={GOLD}>
        <CText x={296} y={38} text="STRONGER POINT" size={10} fill={NAVY} bold />
        <CText x={296} y={53} text="your main argument" size={9} fill="#92400e" italic />
        <CText x={296} y={65} text="(must be stronger)" size={9} fill="#92400e" italic />
      </Box>
      <rect x={10} y={88} width={360} height={24} rx={5} fill={NAVY} />
      <text x={190} y={101} textAnchor="middle" dominantBaseline="middle"
        fontSize={10} fill={WHITE} fontFamily="'Courier New', monospace">
        Although [CONCESSION], [YOUR STRONGER ARGUMENT].
      </text>
    </svg>
  );
}

// ── 5. Evidence & Conclusion Links ───────────────────────────────────
function DiagramEvidence() {
  const boxes = [
    { x: 10,  label: 'EXAMPLE', sub: '(specific fact / data)' },
    { x: 140, label: 'This demonstrates…', sub: '(link phrase)' },
    { x: 270, label: 'ARGUMENT', sub: '(return to your point)' },
  ];
  return (
    <svg viewBox="0 0 380 140" width="100%" style={{ display: 'block' }}>
      {boxes.map((b, i) => (
        <g key={i}>
          <Box x={b.x} y={22} w={110} h={52}
            fill={i === 1 ? LGOLD : i === 0 ? LBLUE : '#f0faf4'}
            stroke={i === 1 ? GOLD : i === 0 ? NAVY : '#16a34a'}>
            <CText x={b.x + 55} y={40} text={b.label} size={9.5}
              fill={i === 1 ? '#92400e' : i === 0 ? NAVY : '#14532d'} bold />
            <CText x={b.x + 55} y={57} text={b.sub} size={8.5}
              fill={i === 1 ? '#b45309' : '#5a6a88'} italic />
          </Box>
          {i < 2 && <Arrow x1={b.x + 113} y1={48} x2={b.x + 135} y2={48} color={GOLD} />}
        </g>
      ))}
      <rect x={10} y={90} width={360} height={24} rx={5} fill={NAVY} />
      <text x={190} y={103} textAnchor="middle" dominantBaseline="middle"
        fontSize={10} fill={WHITE} fontFamily="'Courier New', monospace">
        [Example]. This demonstrates that [argument].
      </text>
    </svg>
  );
}

// ── 6. Academic Conclusions ──────────────────────────────────────────
function DiagramConclusions() {
  const levels = [
    { y: 14, w: 360, label: 'Evidence & Examples (body paragraph)', fill: LBLUE, stroke: NAVY },
    { y: 54, w: 270, label: 'Logical inference from evidence', fill: LGOLD, stroke: GOLD },
    { y: 94, w: 180, label: 'Academic conclusion phrase', fill: NAVY, stroke: NAVY, white: true },
  ];
  return (
    <svg viewBox="0 0 380 140" width="100%" style={{ display: 'block' }}>
      {levels.map((l, i) => {
        const lx = (380 - l.w) / 2;
        return (
          <g key={i}>
            <rect x={lx} y={l.y} width={l.w} height={32} rx={6} fill={l.fill} stroke={l.stroke} strokeWidth={1.5} />
            <text x={190} y={l.y + 17} textAnchor="middle" dominantBaseline="middle"
              fontSize={10} fill={l.white ? WHITE : NAVY} fontWeight={600}
              fontFamily="'Outfit', sans-serif">{l.label}</text>
          </g>
        );
      })}
      <text x={190} y={133} textAnchor="middle" dominantBaseline="middle"
        fontSize={8.5} fill="#9aa3b8" fontFamily="'Outfit', sans-serif">
        What can be concluded… / It can therefore be argued… / The evidence strongly suggests…
      </text>
    </svg>
  );
}

// ── 7. Paraphrasing ──────────────────────────────────────────────────
function DiagramParaphrasing() {
  return (
    <svg viewBox="0 0 380 150" width="100%" style={{ display: 'block' }}>
      {/* Original */}
      <Box x={10} y={14} w={170} h={65} fill="#fff5f5" stroke="#dc2626">
        <CText x={95} y={32} text="ORIGINAL QUESTION" size={9.5} fill="#991b1b" bold />
        <CText x={95} y={50} text='"traffic congestion' size={9} fill="#7f1d1d" italic />
        <CText x={95} y={64} text='has become a problem"' size={9} fill="#7f1d1d" italic />
      </Box>
      {/* Arrow with labels */}
      <Arrow x1={185} y1={47} x2={200} y2={47} color={GOLD} />
      <CText x={192} y={35} text="Replace" size={8} fill="#92400e" bold />
      <CText x={192} y={60} text="nouns/verbs" size={8} fill="#92400e" bold />
      {/* Paraphrased */}
      <Box x={202} y={14} w={170} h={65} fill="#f0faf4" stroke="#16a34a">
        <CText x={287} y={32} text="PARAPHRASED VERSION" size={9.5} fill="#14532d" bold />
        <CText x={287} y={50} text='"urban gridlock has' size={9} fill="#14532d" italic />
        <CText x={287} y={64} text='emerged as a challenge"' size={9} fill="#14532d" italic />
      </Box>
      {/* Rules */}
      <rect x={10} y={94} width={360} height={46} rx={6} fill={NAVY} />
      <CText x={190} y={107} text="Rules: Replace nouns + verbs + adjectives" size={9} fill={WHITE} bold />
      <CText x={190} y={120} text="Change sentence structure   Keep core meaning" size={9} fill="#b8c4d8" />
      <CText x={190} y={133} text="NEVER copy a phrase directly from the question" size={9} fill={LGOLD} bold />
    </svg>
  );
}

const DIAGRAMS = {
  'not-only-but-also':    DiagramNotOnly,
  'relative-pronouns':    DiagramRelative,
  'conditional':          DiagramConditional,
  'contrast-concession':  DiagramContrast,
  'this-shows-that':      DiagramEvidence,
  'academic-conclusions': DiagramConclusions,
  'paraphrasing':         DiagramParaphrasing,
};

export default function StructureDiagram({ structureId }) {
  const Diagram = DIAGRAMS[structureId];
  if (!Diagram) return null;
  return (
    <div style={{ background: '#f7f9fc', border: '1.5px solid #dde3ed', borderRadius: 10, padding: '14px 10px', marginBottom: 16 }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: '#9aa3b8', marginBottom: 8, paddingLeft: 4 }}>
        Structure Diagram
      </div>
      <Diagram />
    </div>
  );
}
